import { Group, Pagination, Select, Text } from '@mantine/core';
import { useTranslation } from '../hooks/useTranslation';

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '20', label: '20' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

export interface PaginationControlsProps {
  /** Current page number (1-indexed) */
  page: number;
  /** Number of items per page */
  limit: number;
  /** Total number of items across all pages */
  total: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when page size (limit) changes */
  onLimitChange: (limit: number) => void;
}

/**
 * Reusable pagination controls component.
 * Includes page navigation, page size selector, and total items display.
 */
export function PaginationControls({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
}: PaginationControlsProps) {
  const { t } = useTranslation();

  const totalPages = Math.ceil(total / limit);

  const handleLimitChange = (value: string | null) => {
    if (value) {
      onLimitChange(Number(value));
    }
  };

  return (
    <Group justify="space-between" align="center" mt="md">
      <Group gap="md" align="center">
        <Select
          label={t('pagination.itemsPerPage')}
          value={limit.toString()}
          onChange={handleLimitChange}
          data={PAGE_SIZE_OPTIONS}
          style={{ width: 100 }}
          size="sm"
          data-testid="page-size-selector"
        />
        <Text size="sm" c="dimmed" data-testid="total-items-count">
          {total} {t('pagination.totalItems')}
        </Text>
      </Group>

      {totalPages > 1 && (
        <Pagination
          value={page}
          onChange={onPageChange}
          total={totalPages}
          size="sm"
          withEdges
          data-testid="pagination"
        />
      )}
    </Group>
  );
}
